import React, { useState, useEffect, useRef } from 'react';
import CommonModal from '../../common/Modal';
import '../PhysicalCardFormModal/index.scss';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  addressValidation,
  newCreateContact
} from '../../../services/Api/Coupon/CouponService';
import Loader from '../../common/Loader';
import { func } from 'prop-types';
import {
  STATE_LIST,
  GTM_PHYSICAL_CARD_MODAL_OPEN,
  GTM_PHYSICAL_CARD_SUCCESS,
  GTM_PHYSICAL_CARD_CARD_SEND_ERROR
} from '../../../constants/defaults';
import { Errors } from '../../../constants/errors';
import { SearchableSelect } from '../../common/SearchableSelect';

const { API } = require('../../../config/' + process.env.REACT_APP_ENV);

const PhysicalCardModal = ({
  t,
  closeModal,
  triggerGTMEvents,
  openRequestModal,
  selectedPharmacy
}) => {
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    address: '',
    addressOptional: '',
    zipCode: '',
    city: '',
    state: '',
    email: '',
    firstNameErr: '',
    lastNameErr: '',
    addressErr: '',
    zipCodeErr: '',
    cityErr: '',
    stateErr: '',
    emailErr: ''
  });
  const [verifyAddressError, setVerifyAddressError] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isButtonDisable, setButtonDisable] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  let errorStatusCode = '';

  const {
    firstName,
    lastName,
    address,
    addressOptional,
    zipCode,
    city,
    state,
    email,
    firstNameErr,
    lastNameErr,
    addressErr,
    zipCodeErr,
    cityErr,
    stateErr,
    emailErr
  } = formState;

  const maxNameLength = 35;
  const maxAddressLength = 30;
  const maxAddressOptionalLength = 30;
  const maxCityLength = 30;
  const mainModalRef = useRef();

  const options = STATE_LIST.map(({ value }) => ({
    label: _.isEmpty(value) ? 'Select' : value,
    value: value
  }));

  useEffect(() => {
    triggerGTMEvents(GTM_PHYSICAL_CARD_MODAL_OPEN);
    validate();
    mainModalRef.current.focus();
  }, []);

  const onClickPaste = e => {
    const paste = e.clipboardData.getData('text');
    let reg = /^-?[0-9]+$/;

    if (!reg.test(paste)) {
      e.preventDefault();
      return true;
    } else {
      return false;
    }
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormState({ ...formState, [name]: value });
    setVerifyAddressError(false);
  };

  const validate = () => {
    if (
      !_.validateName(firstName) ||
      !_.validateName(lastName) ||
      _.isEmpty(address) ||
      _.isEmpty(zipCode) ||
      _.isEmpty(city) ||
      _.isEmpty(state) ||
      !_.validateEmail(email)
    ) {
      setButtonDisable(true);
    } else {
      setButtonDisable(false);
    }
  };

  useEffect(() => {
    validate();
  }, [firstName, lastName, address, zipCode, city, state, email]);

  const handleError = (name, type) => {
    _.setError(setFormState, formState, name, type);
  };

  const handleInputBlur = event => {
    const { name, value } = event.target;
    switch (name) {
      case 'firstName':
        _.validateName(value)
          ? handleError(name, '')
          : handleError(name, 'FName');
        break;
      case 'lastName':
        _.validateName(value)
          ? handleError(name, '')
          : handleError(name, 'LName');
        break;
      case 'address':
        !_.isEmpty(value)
          ? handleError(name, '')
          : handleError(name, 'Required');
        break;
      case 'zipCode':
        !_.isEmpty(value) && value.length === 5 && zipCode !== '00000'
          ? handleError(name, '')
          : handleError(name, 'Zip');
        break;
      case 'city':
        !_.isEmpty(value)
          ? handleError(name, '')
          : handleError(name, 'Required');
        break;
      case 'email':
        setFormState({
          ...formState,
          emailErr: _.validateEmail(value) ? '' : Errors.EmailPhysical
        });
        break;
      default:
        handleError(name, '');
    }
  };

  const getStatusCode = error => {
    errorStatusCode = error;
  };

  const onSubmit = async event => {
    event.preventDefault();
    try {
      setLoading(true);
      let finalApiData = {
        ...formState,
        pid: selectedPharmacy.PartnerUser,
        pPass: selectedPharmacy.PartnerPassword,
        pharmacy: selectedPharmacy.PharmacyName
      };
      const validateAddress = API?.ALLOW_ADDRESS_V3
        ? await addressValidation(
            {
              address1: formState.address,
              address2: formState.addressOptional,
              zipCode: formState.zipCode,
              state: formState.state,
              city: formState.city
            },
            getStatusCode
          )
        : { status: 200, data: { success: true } };
      if (validateAddress.status == 200 && validateAddress.data?.success) {
        const contactData = await newCreateContact(finalApiData, getStatusCode);
        if (contactData.status == 200) {
          triggerGTMEvents(GTM_PHYSICAL_CARD_SUCCESS);
          closeModal();
          openRequestModal();
        }
      } else {
        throw validateAddress?.status;
      }
    } catch (err) {
      console.log(err);
      setVerifyAddressError(true);
      setButtonDisable(true);
      let eventObj = {
        address1: formState.address,
        address2: formState.addressOptional,
        zipCode: formState.zipCode,
        state: formState.state,
        city: formState.city,
        errorCode: errorStatusCode
      };
      triggerGTMEvents(GTM_PHYSICAL_CARD_CARD_SEND_ERROR, eventObj);
    } finally {
      setLoading(false);
    }
  };

  const selectHandler = value => {
    if (_.isEqual(value, 'Select')) {
      setFormState({
        stateErr: t('This value is required')
      });
    } else {
      setFormState({
        ...formState,
        state: value,
        stateErr: !_.isEmpty(value) ? '' : t('This value is required')
      });
    }

    setVerifyAddressError(false);
  };

  useEffect(() => {
    setTimeout(() => {
      document.body.style.overflow = 'hidden';
    }, 10);
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const restrictAlphabets = e => {
    const re = /[0-9]+/g;
    if (!re.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="physicalCardModal" ref={mainModalRef} tabIndex="0">
      <CommonModal
        closeDialogHandler={closeModal}
        closeButtonIMG="icon-close-white.svg"
      >
        <div className="Heading">
          <p className="title">
            {t('Enter your info below to receive your savings kit.')}
          </p>
        </div>
        <div className="formWrapper">
          <p className="mailText">{t('Mailing Address')}</p>
          <input
            placeholder={t('First Name')}
            className={`dialogComponent themeFocus ${
              firstNameErr && 'invalid'
            }`}
            tabIndex="0"
            name={'firstName'}
            value={firstName}
            onChange={handleInputChange}
            onBlur={e => {
              handleInputBlur(e);
              validate();
            }}
            autoComplete="off"
            maxLength={maxNameLength}
          />
          <span id={firstNameErr} className="label-error" role="alert">
            {t(firstNameErr)}
          </span>
          <input
            placeholder={t('Last Name')}
            className={`dialogComponent themeFocus ${lastNameErr && 'invalid'}`}
            name={'lastName'}
            value={lastName}
            onChange={handleInputChange}
            onBlur={e => {
              handleInputBlur(e);
              validate();
            }}
            autoComplete="off"
            maxLength={maxNameLength}
          />
          <span id={lastNameErr} className="label-error" role="alert">
            {t(lastNameErr)}
          </span>
          <input
            placeholder={t('Address')}
            className={`dialogComponent themeFocus ${addressErr && 'invalid'}`}
            name={'address'}
            value={address}
            onChange={handleInputChange}
            onBlur={e => {
              handleInputBlur(e);
              validate();
            }}
            autoComplete="off"
            maxLength={maxAddressLength}
          />
          <span id={addressErr} className="label-error" role="alert">
            {t(addressErr)}
          </span>
          <input
            placeholder={t('Address (2) Optional')}
            className="dialogComponent themeFocus"
            name={'addressOptional'}
            value={addressOptional}
            onChange={handleInputChange}
            autoComplete="off"
            maxLength={maxAddressOptionalLength}
          />
          <div className="cityZipStateWrapper">
            <div className="zipdesktopWrapper desktop-only">
              <input
                placeholder={t('Zip Code')}
                className={`desktopzipInput dialogComponent themeFocus ${
                  zipCodeErr && 'invalid'
                }`}
                name={'zipCode'}
                value={zipCode}
                onChange={handleInputChange}
                onBlur={e => {
                  handleInputBlur(e);
                  validate();
                }}
                autoComplete="off"
                maxLength={5}
                onKeyPress={restrictAlphabets}
                onPaste={onClickPaste}
              />
              <span id={zipCodeErr} className="label-error" role="alert">
                {t(zipCodeErr)}
              </span>
            </div>
            <div className="cityWrapper">
              <input
                placeholder={t('City')}
                className={`cityInput dialogComponent themeFocus ${
                  cityErr && 'invalid'
                }`}
                name={'city'}
                value={city}
                onChange={handleInputChange}
                onBlur={e => {
                  handleInputBlur(e);
                  validate();
                }}
                autoComplete="off"
                maxLength={maxCityLength}
              />
              <span id={cityErr} className="label-error" role="alert">
                {t(cityErr)}
              </span>
            </div>
            <div className="stateWrapper">
              <SearchableSelect
                options={options}
                value={selectedState}
                onChange={setSelectedState}
                placeholder={t('State')}
                selectHandler={selectHandler}
              />
              <span id={stateErr} className="label-error" role="alert">
                {t(stateErr)}
              </span>
            </div>
            <div className="zipWrapper mobile-only">
              <input
                placeholder={t('Zip Code')}
                className={`mobilezipInput dialogComponent ${
                  zipCodeErr && 'invalid'
                }`}
                name="zipCode"
                value={zipCode}
                onChange={handleInputChange}
                onBlur={e => {
                  handleInputBlur(e);
                  validate();
                }}
                autoComplete="off"
                maxLength={5}
                onKeyPress={restrictAlphabets}
                onPaste={onClickPaste}
              />
              <span id={zipCodeErr} className="label-error" role="alert">
                {t(zipCodeErr)}
              </span>
            </div>
          </div>
          {verifyAddressError && (
            <p className="label-error">
              {t('We are unable to verify this address. ')}
              <br />
              {t('Please review your address information.')}
            </p>
          )}
          <div className="emailInputWrap">
            <p className="mailText">{t('Email Address')}</p>
            <input
              placeholder="example@gmail.com"
              className={`dialogComponent themeFocus ${emailErr && 'invalid'}`}
              name={'email'}
              value={email}
              onChange={handleInputChange}
              onBlur={e => {
                handleInputBlur(e);
                validate();
              }}
              autoComplete="email"
            />
            <span id={emailErr} className="label-error" role="alert">
              {t(emailErr)}
            </span>
          </div>
          {/* <div className="newsLetterWrap">
            <input
              type="checkbox"
              id="newsLetter"
              tabIndex="0"
              className="dialogComponent themeFocus"
              onClick={() =>
                triggerGTMEvents(GTM_PHYSICAL_CARD_MODAL_NEWS_LETTER_CHECK)
              }
            />
            <label>{t("Send me the SingleCare Newsletter")}</label>
          </div> */}
          {isLoading ? (
            <div className="centerBlock">
              <Loader />
            </div>
          ) : (
            <button
              className="sendCardButton gradientPink themeFocus dialogComponent"
              tabIndex="0"
              disabled={isButtonDisable}
              onClick={onSubmit}
            >
              {t('Send me a physical card')}
            </button>
          )}
          <p className="bottomText">{t('bottom note')}</p>
        </div>
      </CommonModal>
    </div>
  );
};
PhysicalCardModal.prototype = {
  closeModal: func,
  triggerGTMEvents: func,
  openRequestModal: func
};
PhysicalCardModal.defaultProps = {
  closeModal: () => {},
  triggerGTMEvents: () => {},
  openRequestModal: () => {}
};
export default withTranslation()(PhysicalCardModal);
